// @ts-ignore
import * as L from 'leaflet/dist/leaflet-src.esm.js';
import { DefaultBaseMapLayer } from '../layerModel/baseLayers/defaultBaseMapLayer';
import { basePaneName, LayerFactory, overlayPaneName } from './layerFactory';
import { OverlayEmptyLayer } from '../layerModel/overlayLayers/overlayEmptyLayer';
export class LayersManager {
    constructor() {
        this.map = null;
        this.baseLayers = [];
        this.overlayLayers = [];
        this.overlayLayerMinZIndex = 1000;
    }
    /**
     * This function creates a control object, assigns it to the object property of the class, and
     * returns the control object.
     * @param {Object} baseLayers - Object - The base layers object.
     * @param {Object} overlayLayers - Object - The overlay layers object.
     * @returns The controlObject is being returned.
     */
    createControler(map, baseLayers) {
        this.map = map;
        this.baseLayers = this.getLeafletBaseLayers(baseLayers);
        this.overlayLayers = [];
        this.addMapPanes();
        this.initializeActiveBaseLayerInMap(baseLayers);
    }
    /**
     * Set a base layer as active. Other base layers are deleted from map
     * And this base layer is added in order to be the only base layer active in map
     * @param activeBaseLayer The base layer that we want to show in map
     */
    changeActiveBaseLayer(activeBaseLayer) {
        let layerIsAlreadyActiveInMap = false;
        // Remove current base layers in map that are not the active one
        this.map.eachLayer((layer) => {
            const layerId = layer.options.id;
            if (this.isBaseLayer(layerId)) {
                if (activeBaseLayer.id !== layerId) {
                    // Remove the base laer
                    this.map.removeLayer(layer);
                }
                else {
                    // The layer we want to activate is already added to map
                    layerIsAlreadyActiveInMap = true;
                }
            }
        });
        if (!layerIsAlreadyActiveInMap) {
            // If layer we want to activate is not ni map, we activate it
            const newActiveLayer = this.getBaseLayerById(activeBaseLayer.id);
            if (newActiveLayer) {
                this.map.addLayer(newActiveLayer);
            }
        }
    }
    /**
     * Ads an array of overlayLayers to map
     * @param overlayLayers Overlay layers
     */
    addOverlayLayers(overlayLayers) {
        overlayLayers.forEach((oL) => {
            this.addOverlayLayerToMap(oL);
        });
    }
    /**
     * Removes an array of overlayLayers to map
     * @param overlayLayers Overlay layers
     */
    removeOverlayLayers(overlayLayers) {
        overlayLayers.forEach((oL) => {
            this.removeOverlayLayerToMap(oL);
        });
    }
    /**
     * Manages the changes in an overlay layer
     * @param modified The modified layers and an array of fields that have changed for each layer
     */
    manageOverlayLayersChanges(modified) {
        modified.forEach((el) => {
            if (el.modifiedFields.indexOf('reset') > -1) {
                // Reset means we have to remove and add this layer again because some
                // immutable field has changed
                this.removeOverlayLayers([el.overlayLayer]);
                this.addOverlayLayers([el.overlayLayer]);
            }
            else {
                if (el.modifiedFields.indexOf('visibility') > -1) {
                    this.setOverlayLayerVisibility(el.overlayLayer, el.overlayLayer.isVisible);
                }
                if (el.modifiedFields.indexOf('opacity') > -1) {
                    this.setOverlayLayerOpacity(el.overlayLayer, el.overlayLayer.opacity ? el.overlayLayer.opacity : 1);
                }
            }
        });
    }
    /**
     * If the object is not null, remove it and set it to null.
     * @returns The object is being returned.
     */
    removeControler() {
        this.map.eachLayer((layer) => {
            this.map.removeLayer(layer);
        });
        this.baseLayers = [];
        this.overlayLayers = [];
        this.map = null;
    }
    /**
     * Looks for all overlayLayers and updates its layer order
     * setting a zIndex for each layer. The first element in the array will be the one
     * with highest zIndex, so it will be on top.
     * @param overlayLayers The current overlay layers in the desired order.
     */
    updateLayerOrder(overlayLayers) {
        let currentZIndex = this.overlayLayerMinZIndex;
        for (let i = overlayLayers.length - 1; i >= 0; i -= 1) {
            const overlayLayer = overlayLayers[i];
            let layerPane = this.map.getPane(overlayLayer.id);
            if (!layerPane) {
                this.map.createPane(overlayLayer.id);
                layerPane = this.map.getPane(overlayLayer.id);
            }
            layerPane.style.zIndex = currentZIndex;
            currentZIndex += 1;
        }
    }
    /**
     * Change visibility of a layer in map
     * @param overlayLayer The layer we want to change its visibility
     * @param visibility the new visibility of the layer
     */
    setOverlayLayerVisibility(overlayLayer, visibility) {
        const oL = this.getOverlayLayerById(overlayLayer.id);
        if (oL instanceof OverlayEmptyLayer) {
            if (!visibility) {
                // If layer is present in map and shouldn't be visible remove it
                this.removeOverlayLayerToMap(oL);
            }
            else {
                // If layer is not present in map and should be visible add to map
                this.addOverlayLayerToMap(oL);
            }
        }
        else if (oL && !visibility) {
            // If layer is present in map and shouldn't be visible remove it
            this.removeOverlayLayerToMap(overlayLayer);
        }
        else if (!oL && visibility) {
            // If layer is not present in map and should be visible add to map
            this.addOverlayLayerToMap(overlayLayer);
        }
    }
    /**
     * Change opacity of a layer in map
     * @param overlayLayer The layer we want to change its opacity
     * @param opacity the new opacity of the layer
     */
    setOverlayLayerOpacity(overlayLayer, opacity) {
        const oL = this.getOverlayLayerById(overlayLayer.id);
        if (oL) {
            if (overlayLayer.type === 'OverlayCustomLayer') {
                oL.eachLayer((x) => {
                    x.setOpacity(opacity);
                });
            }
            else if (oL instanceof OverlayEmptyLayer) {
                oL.setOpacity(this.map, opacity);
            }
            else {
                oL.setOpacity(opacity);
            }
        }
    }
    /**
     * We add map panes in order to set different z-index for base layers
     * and overlay layers
     */
    addMapPanes() {
        // Create pane(for grouping layer order) for base layers
        this.map.createPane(basePaneName);
        // Create pane(for grouping layer order) for element layers
        this.map.createPane(overlayPaneName);
        const basePane = this.map.getPane(basePaneName);
        if (basePane) {
            basePane.style.zIndex = '100';
        }
        const overlayPane = this.map.getPane(overlayPaneName);
        if (overlayPane) {
            overlayPane.style.zIndex = `${this.overlayLayerMinZIndex}`;
        }
    }
    /**
     * Add the current active layer to map at map initialization
     */
    initializeActiveBaseLayerInMap(baseLayers) {
        let activeBaseLayer = baseLayers.find((bL) => bL.isActive);
        if (!activeBaseLayer) {
            activeBaseLayer = baseLayers.find((_, i) => i === 0);
            if (activeBaseLayer) {
                activeBaseLayer.isActive = true;
            }
        }
        const baseLayerActive = this.baseLayers.find((el) => el.options.id === (activeBaseLayer === null || activeBaseLayer === void 0 ? void 0 : activeBaseLayer.id));
        baseLayerActive.addTo(this.map);
    }
    /**
     * Set the baseLayer that get from the baseLayer property and add it to
     * baseMapControl
     */
    getLeafletBaseLayers(currentBaseLayers) {
        const baseLayers = [];
        const layerFactoryHandler = new LayerFactory();
        // If doesn't provide any baseLayer, a default basic layer is added
        if (currentBaseLayers.length === 0) {
            currentBaseLayers.push(new DefaultBaseMapLayer('Basic', false));
        }
        currentBaseLayers.forEach((layer) => {
            try {
                const baseLayer = layerFactoryHandler.create(layer);
                baseLayers.push(baseLayer);
            }
            catch (e) {
                console.error(e);
            }
        });
        return baseLayers;
    }
    createLayer(overlayLayer) {
        const existingLayer = this.overlayLayers.filter((layer) => layer.id === overlayLayer.id);
        if (existingLayer.length > 0) {
            return existingLayer;
        }
        const layerFactoryHandler = new LayerFactory();
        return layerFactoryHandler.createOverlayLayer(overlayLayer)
            .map((layer) => {
            const item = layer;
            if (item.options) {
                item.options.pane = overlayLayer.id;
            }
            return item;
        });
    }
    /**
     * Transform overlay layer into a leaflet layer and adds it to map
     * @param overlayLayer The overlay layer
     */
    addOverlayLayerToMap(overlayLayer) {
        if (overlayLayer.isVisible || (overlayLayer.type === 'OverlayEmptyLayer')) {
            const overlayLeafletLayer = this.createLayer(overlayLayer);
            const overlayLayersWMS = [];
            const pane = this.map.getPane(overlayLayer.id);
            if (pane === undefined) {
                this.map.createPane(overlayLayer.id);
            }
            if (overlayLayer.type === 'OverlayEmptyLayer') {
                if (this.overlayLayers.filter((layer) => layer.id === overlayLayer.id).length === 0) {
                    overlayLeafletLayer.forEach((item) => this.overlayLayers.push(item));
                }
                overlayLeafletLayer.forEach((layer) => layer.show(this.map));
            }
            else {
                overlayLeafletLayer.forEach((layer) => {
                    overlayLayersWMS.push(layer.addTo(this.map));
                });
                if (overlayLayer.type === 'OverlayBasicLayer') {
                    this.overlayLayers.push(...overlayLeafletLayer);
                    overlayLeafletLayer[0].setOpacity(overlayLayer.opacity);
                }
                if (overlayLayer.type === 'OverlayCustomLayer') {
                    // Add the custom layers into a group layer
                    const groupLayer = L.layerGroup(overlayLayersWMS, { pane: overlayLayer.id, id: overlayLayer.id }).addTo(this.map);
                    this.overlayLayers.push(groupLayer);
                    groupLayer.eachLayer((x) => x.setOpacity(overlayLayer.opacity));
                }
            }
        }
    }
    /**
     * Transform overlay layer into a leaflet layer and adds it to map
     * @param overlayLayer The overlay layer
     */
    removeOverlayLayerToMap(overlayLayer) {
        if (overlayLayer instanceof OverlayEmptyLayer) {
            overlayLayer.remove(this.map);
        }
        else {
            this.removeWmsLayer(overlayLayer);
        }
    }
    removeWmsLayer(overlayLayer) {
        // Find layer to remove in map
        const layerToRemoveFromMap = [];
        this.map.eachLayer((layer) => {
            if (layer.options.id === overlayLayer.id) {
                layerToRemoveFromMap.push(layer);
            }
        });
        // If layer to remove is found, delete it form map and from the array of
        // overlaylayers in layerManager
        if (layerToRemoveFromMap.length > 0) {
            layerToRemoveFromMap.forEach((layer) => {
                // Remove fom map
                this.map.removeLayer(layer);
                // Remove from inner array
                const indexItem = this.overlayLayers
                    .findIndex((oL) => oL.options !== undefined && oL.options.id === overlayLayer.id);
                if (indexItem > -1) {
                    this.overlayLayers.splice(indexItem, 1);
                }
            });
        }
    }
    /**
     * Checks if an id of a layer is from a base layer fro the ones stored in layermanager or not
     * @param layerId Id of the layer we want to check
     * @returns A Boolean
     */
    isBaseLayer(layerId) {
        return this.baseLayers.find((bl) => bl.options.id === layerId) != null;
    }
    /**
     * Returns the base layer by id. If not found returns undefined.
     * @param layerId The layer id
     */
    getBaseLayerById(layerId) {
        return this.baseLayers.find((bl) => bl.options.id === layerId);
    }
    /**
     * Returns the overlay layer by id. If not found returns undefined.
     * @param layerId The layer id
     */
    getOverlayLayerById(layerId) {
        // search for wms layers.
        let result = this.overlayLayers
            .find((bl) => { var _a; return ((_a = bl.options) === null || _a === void 0 ? void 0 : _a.id) === layerId; });
        // if no wms layers check fof Polygons layers.
        if (result === undefined || result.length === 0) {
            result = this.overlayLayers
                .find((bl) => bl.options === undefined && bl.id === layerId);
        }
        return result;
    }
}
//# sourceMappingURL=layersManager.js.map